import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { RestartAlt } from '@mui/icons-material'
import { useCallback, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useUpdateClimate } from '~/api/supabase-mutations'
import { supabaseGetter } from '~/api/supabase-getter'
import { FrostDateEditor } from '~/farm/add-farm/frost-date-editor'
import NumberField from '~/components/number-field/NumberField'
import { useTemperatureUnit } from '~/common/use-temperature-unit'
import { useFeatures } from '~/common/use-features'

// isTemp: absolute temperature value (convert C↔F)
// isDelta: temperature range/delta (scale only, no offset)
const ClimateField = ({ label, fieldKey, value, onChange, isTemp = false, isDelta = false, toDisplay, toCelsius, toDisplayDelta, fromDisplayDelta }) => {
  const displayValue = isTemp
    ? toDisplay(value)
    : isDelta
      ? toDisplayDelta(value)
      : value

  return (
    <Box sx={{ flex: '1 1 160px', minWidth: 130 }}>
      <NumberField
        label={label}
        value={displayValue ?? null}
        onValueChange={(val) => {
          if (val !== null) {
            const stored = isTemp
              ? toCelsius(val)
              : isDelta
                ? fromDisplayDelta(val)
                : val
            onChange(fieldKey, stored)
          }
        }}
        size="small"
      />
    </Box>
  )
}

export const FarmClimateCard = ({ initialClimate }) => {
  const [climate, setClimate] = useState(initialClimate || null)
  const [pendingChanges, setPendingChanges] = useState({})
  const [frostResetKey, setFrostResetKey] = useState(0)
  const { features } = useFeatures()
  const { updateClimate, loading: saving } = useUpdateClimate()
  const { enqueueSnackbar } = useSnackbar()
  const { unit, toDisplay, toCelsius, toDisplayDelta, fromDisplayDelta, displayThreshold } = useTemperatureUnit()

  const isDirty = Object.entries(pendingChanges).some(
    ([key, val]) => Number(val) !== Number(climate?.[key])
  )

  const handleFieldChange = useCallback((key, value) => {
    setPendingChanges(prev => ({ ...prev, [key]: value }))
  }, [])

  const handleFrostReset = useCallback(() => {
    setPendingChanges(prev => ({ ...prev }))
  }, [])

  const handleReset = useCallback(() => {
    setPendingChanges({})
    setFrostResetKey(k => k + 1)
  }, [])

  const handleSave = useCallback(async () => {
    if (!climate?.id || !isDirty) return
    const changes = Object.fromEntries(
      Object.entries(pendingChanges).filter(([key, val]) => Number(val) !== Number(climate?.[key]))
    )
    await updateClimate(climate.id, { ...changes, updated_at: new Date() }, {
      onSuccess: () => {
        supabaseGetter.climates.invalidate()
        enqueueSnackbar('Climate updated', { variant: 'success' })
        setClimate(prev => prev ? { ...prev, ...changes } : prev)
        setPendingChanges({})
      },
      onError: (err) => enqueueSnackbar(err.message, { variant: 'error' }),
    })
  }, [climate, isDirty, pendingChanges, updateClimate, enqueueSnackbar])

  if (!climate) return null

  const fieldProps = { toDisplay, toCelsius, toDisplayDelta, fromDisplayDelta, onChange: handleFieldChange }

  return (
    <Card variant="outlined">
      <CardHeader title="Climate" sx={{ pb: 0 }} />
      <CardContent>
        <Stack spacing={2}>
          <Alert severity="warning" sx={{ py: 0.5 }}>
            Editing these values affects crop scheduling calculations for this farm.
          </Alert>

          {/* Frost Dates */}
          <FrostDateEditor
            key={`${climate.id}-${frostResetKey}`}
            defaultLfd={climate.avg_lfd}
            defaultFfd={climate.avg_ffd}
            stdLfd={climate.std_lfd}
            stdFfd={climate.std_ffd}
            onLfdChange={(doy) => handleFieldChange('avg_lfd', doy)}
            onFfdChange={(doy) => handleFieldChange('avg_ffd', doy)}
            onReset={handleFrostReset}
          />

          <Divider />

          {/* Frost Window */}
          <Box>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              Frost Window
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1.5}>
              <ClimateField label="Avg frost-free window (days)" fieldKey="avg_ffw" value={pendingChanges.avg_ffw ?? climate.avg_ffw} {...fieldProps} />
              <ClimateField label="Min frost-free window (days)" fieldKey="min_ffw" value={pendingChanges.min_ffw ?? climate.min_ffw} {...fieldProps} />
              <ClimateField label="Std dev last frost (days)" fieldKey="std_lfd" value={pendingChanges.std_lfd ?? climate.std_lfd} {...fieldProps} />
              <ClimateField label="Std dev first frost (days)" fieldKey="std_ffd" value={pendingChanges.std_ffd ?? climate.std_ffd} {...fieldProps} />
              <ClimateField label="False spring risk (0–1)" fieldKey="fs_risk" value={pendingChanges.fs_risk ?? climate.fs_risk} {...fieldProps} />
            </Stack>
          </Box>

          <Divider />

          {/* Heat Accumulation */}
          <Box>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              Heat Accumulation
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1.5}>
              <ClimateField label={`GDD base ${displayThreshold(5)}`} fieldKey="avg_gdd5" value={pendingChanges.avg_gdd5 ?? climate.avg_gdd5} {...fieldProps} />
              <ClimateField label={`GDD base ${displayThreshold(10)}`} fieldKey="avg_gdd10" value={pendingChanges.avg_gdd10 ?? climate.avg_gdd10} {...fieldProps} />
              <ClimateField label={`Avg daily GDD base ${displayThreshold(5)}`} fieldKey="avg_daily_gdd5" value={pendingChanges.avg_daily_gdd5 ?? climate.avg_daily_gdd5} {...fieldProps} />
              <ClimateField label={`Avg daily GDD base ${displayThreshold(10)}`} fieldKey="avg_daily_gdd10" value={pendingChanges.avg_daily_gdd10 ?? climate.avg_daily_gdd10} {...fieldProps} />
              <ClimateField label={`Optimal days (${displayThreshold(10)}–${displayThreshold(30)})`} fieldKey="avg_optimal_days" value={pendingChanges.avg_optimal_days ?? climate.avg_optimal_days} {...fieldProps} />
              <ClimateField label={`Heat days >${displayThreshold(32)}`} fieldKey="avg_heat_days32" value={pendingChanges.avg_heat_days32 ?? climate.avg_heat_days32} {...fieldProps} />
              <ClimateField label={`Heat days >${displayThreshold(38)}`} fieldKey="avg_heat_days38" value={pendingChanges.avg_heat_days38 ?? climate.avg_heat_days38} {...fieldProps} />
              <ClimateField label={`Tropical nights (>${displayThreshold(20)})`} fieldKey="avg_trop_nights" value={pendingChanges.avg_trop_nights ?? climate.avg_trop_nights} {...fieldProps} />
              <ClimateField label={`Chill days (${displayThreshold(0)}–${displayThreshold(7)})`} fieldKey="avg_chill_days" value={pendingChanges.avg_chill_days ?? climate.avg_chill_days} {...fieldProps} />
            </Stack>
          </Box>

          <Divider />

          {/* Temperature Extremes */}
          <Box>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              Temperature Extremes ({unit})
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1.5}>
              <ClimateField label="Avg max temp" fieldKey="avg_max_temp" value={pendingChanges.avg_max_temp ?? climate.avg_max_temp} isTemp {...fieldProps} />
              <ClimateField label="Avg min temp" fieldKey="avg_min_temp" value={pendingChanges.avg_min_temp ?? climate.avg_min_temp} isTemp {...fieldProps} />
              <ClimateField label="Abs max temp" fieldKey="abs_max_temp" value={pendingChanges.abs_max_temp ?? climate.abs_max_temp} isTemp {...fieldProps} />
              <ClimateField label="Abs min temp" fieldKey="abs_min_temp" value={pendingChanges.abs_min_temp ?? climate.abs_min_temp} isTemp {...fieldProps} />
              <ClimateField label="Avg diurnal range" fieldKey="avg_dtr" value={pendingChanges.avg_dtr ?? climate.avg_dtr} isDelta {...fieldProps} />
            </Stack>
          </Box>

          {/* Actions */}
          <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
            <Tooltip title="Reset to saved values">
              <span>
                <IconButton onClick={handleReset} disabled={!isDirty}>
                  <RestartAlt />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={features['edit_farm'] || !isDirty || saving}
            >
              {saving ? 'Saving…' : 'Save Climate Changes'}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}
